import React from "react";
import AdminHeader from "../components/AdminHeader";
import { Link } from "react-router-dom";
import withRouter from "../../../helpers/withRouter";
import { getUserConversations } from "../../../api/rest-apis";
import { getMembershipBadgeMeta } from "../../../helpers/membershipBadge";
import type {
  AdminConversationUser,
  Conversation,
  MembershipBadge,
} from "../../../interfaces/rest-api";

interface UserMessagesProps {
  id: string;
}

interface ConversationSummary {
  totalConversations: number;
  totalMessages: number;
  assistantIds: number;
  characterIds: number;
  characterNames: string[];
  lastUpdated: string | null;
}

interface ConversationPagination {
  page: number;
  limit: number;
  totalConversations: number;
  totalPages: number;
}

type ConversationUserDetails = AdminConversationUser;

interface UserMessagesState {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  user: ConversationUserDetails;
  loading: boolean;
  sortBy: 'latest' | 'tokens';
  conversationSearch: string;
  messageSearch: string;
  messageRoleFilter: 'all' | 'user' | 'assistant' | 'system';
  expandedMessages: Record<string, boolean>;
  summary: ConversationSummary;
  pagination: ConversationPagination;
  error: string | null;
}

class UserMessages extends React.Component<
  UserMessagesProps,
  UserMessagesState
> {
  state: UserMessagesState = {
    conversations: [],
    selectedConversation: null,
    user: {
      _id: this.props.id,
      originalId: this.props.id,
      firstName: "",
      lastName: "",
      email: "",
      membershipBadge: "subscriber",
      isActivelyPaying: false,
      ranOutOfTokens: false,
      tokenBadge: null,
      plusTrialEnded: false,
    },
    loading: true,
    sortBy: 'latest',
    conversationSearch: "",
    messageSearch: "",
    messageRoleFilter: "all",
    expandedMessages: {},
    summary: {
      totalConversations: 0,
      totalMessages: 0,
      assistantIds: 0,
      characterIds: 0,
      characterNames: [],
      lastUpdated: null
    },
    pagination: {
      page: 1,
      limit: 25,
      totalConversations: 0,
      totalPages: 0
    },
    error: null,
  };

  async componentDidMount() {
    this.fetchConversations();
  }

  componentDidUpdate(prevProps: UserMessagesProps) {
    if (prevProps.id !== this.props.id) {
      this.setState(
        {
          conversations: [],
        selectedConversation: null,
        conversationSearch: "",
        messageSearch: "",
        messageRoleFilter: "all",
        expandedMessages: {},
        user: {
          _id: this.props.id,
          originalId: this.props.id,
          firstName: "",
            lastName: "",
            email: "",
            membershipBadge: "subscriber",
            isActivelyPaying: false,
            ranOutOfTokens: false,
            tokenBadge: null,
            plusTrialEnded: false,
          },
          summary: {
            totalConversations: 0,
            totalMessages: 0,
            assistantIds: 0,
            characterIds: 0,
            characterNames: [],
            lastUpdated: null,
          },
          pagination: {
            ...this.state.pagination,
            page: 1,
            totalConversations: 0,
            totalPages: 0,
          },
          error: null,
        },
        () => this.fetchConversations()
      );
    }
  }

  fetchConversations = async (page?: number, limit?: number) => {
    const {
      pagination: { page: currentPage, limit: currentLimit },
    } = this.state;

    const nextPage = page ?? currentPage;
    const nextLimit = limit ?? currentLimit;

    this.setState({ loading: true, error: null });

    try {
      const response = await getUserConversations(this.props.id, {
        page: nextPage,
        limit: nextLimit,
        includeSummary: true,
      });

      const conversations: Conversation[] = response?.data || [];
      const summary = response?.summary || {};
      const userDetails = response?.user;
      const meta = response?.meta || {};
      const metaTotalConversations =
        typeof meta.totalConversations === "number"
          ? meta.totalConversations
          : typeof meta.total === "number"
          ? meta.total
          : undefined;
      const overallTotalConversations =
        typeof summary.totalConversations === "number"
          ? summary.totalConversations
          : typeof metaTotalConversations === "number"
          ? metaTotalConversations
          : conversations.length;
      const metaLimitValue =
        typeof meta.limit === "number"
          ? meta.limit
          : typeof meta.pageSize === "number"
          ? meta.pageSize
          : nextLimit;
      const metaPageValue =
        typeof meta.page === "number"
          ? meta.page
          : typeof meta.currentPage === "number"
          ? meta.currentPage
          : nextPage;

      const totalPages =
        typeof meta.totalPages === "number" && meta.totalPages >= 0
          ? meta.totalPages
          : typeof meta.pages === "number" && meta.pages >= 0
          ? meta.pages
          : metaLimitValue > 0
          ? Math.ceil(overallTotalConversations / metaLimitValue)
          : conversations.length ? 1 : 0;

      this.setState((prevState) => {
        const selectedConversation =
          prevState.selectedConversation &&
          conversations.some(
            (conversation) =>
              conversation._id === prevState.selectedConversation?._id
          )
            ? prevState.selectedConversation
            : conversations[0] || null;

        const previousUser: ConversationUserDetails =
          prevState.user || {
            _id: this.props.id,
            originalId: this.props.id,
            membershipBadge: "subscriber",
            isActivelyPaying: false,
            ranOutOfTokens: false,
            tokenBadge: null,
            plusTrialEnded: false,
          };

        const { value: membershipBadge } = getMembershipBadgeMeta(
          (userDetails?.membershipBadge ??
            previousUser.membershipBadge) as MembershipBadge | undefined
        );

        const resolvedUser: ConversationUserDetails = {
          ...previousUser,
          ...userDetails,
          originalId: this.props.id,
          membershipBadge,
          highestBadge: (userDetails?.highestBadge ??
            previousUser.highestBadge) as MembershipBadge | undefined,
          isActivelyPaying:
            typeof userDetails?.isActivelyPaying === "boolean"
              ? userDetails.isActivelyPaying
              : previousUser.isActivelyPaying ?? false,
          ranOutOfTokens:
            typeof userDetails?.ranOutOfTokens === "boolean"
              ? userDetails.ranOutOfTokens
              : previousUser.ranOutOfTokens ?? false,
          tokenBadge:
            typeof userDetails?.tokenBadge === "string"
              ? userDetails.tokenBadge
              : previousUser.tokenBadge ?? null,
          plusTrialEnded:
            typeof userDetails?.plusTrialEnded === "boolean"
              ? userDetails.plusTrialEnded
              : previousUser.plusTrialEnded ?? false,
        };

        return {
          conversations,
          selectedConversation,
          user: resolvedUser,
          summary: {
            totalConversations:
              summary.totalConversations ?? overallTotalConversations ?? 0,
            totalMessages: summary.totalMessages ?? 0,
            assistantIds: summary.assistantIds ?? 0,
            characterIds: summary.characterIds ?? 0,
            characterNames: Array.isArray(summary.characterNames)
              ? summary.characterNames
              : [],
            lastUpdated: summary.lastUpdated ?? null,
          },
          pagination: {
            page: metaPageValue,
            limit: metaLimitValue,
            totalConversations: overallTotalConversations,
            totalPages,
          },
          loading: false,
        };
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to load conversations.";

      this.setState({
        conversations: [],
        selectedConversation: null,
        conversationSearch: "",
        messageSearch: "",
        messageRoleFilter: "all",
        loading: false,
        error: errorMessage,
        summary: {
          totalConversations: 0,
          totalMessages: 0,
          assistantIds: 0,
          characterIds: 0,
          characterNames: [],
          lastUpdated: null,
        },
        pagination: {
          page: 1,
          limit: nextLimit,
          totalConversations: 0,
          totalPages: 0,
        },
        expandedMessages: {},
      });
    }
  }

  selectConversation = (conversation: Conversation) => {
    this.setState({
      selectedConversation: conversation,
      messageSearch: "",
      messageRoleFilter: "all",
      expandedMessages: {}
    });
  };

  setSortBy = (sortBy: 'latest' | 'tokens') => {
    this.setState({ sortBy });
  };

  setConversationSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ conversationSearch: event.target.value });
  };

  setMessageSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ messageSearch: event.target.value });
  };

  setMessageRoleFilter = (filter: 'all' | 'user' | 'assistant' | 'system') => {
    this.setState({ messageRoleFilter: filter });
  };

  toggleExpandedMessage = (messageKey: string) => {
    this.setState((prevState) => ({
      expandedMessages: {
        ...prevState.expandedMessages,
        [messageKey]: !prevState.expandedMessages[messageKey],
      },
    }));
  };

  changePage = (page: number) => {
    const {
      pagination: { totalPages },
    } = this.state;

    if (page < 1 || (totalPages && page > totalPages)) {
      return;
    }

    this.setState(
      (prevState) => ({
        pagination: {
          ...prevState.pagination,
          page,
        },
      }),
      () => this.fetchConversations(page)
    );
  };

  changeLimit = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLimit = parseInt(event.target.value, 10);

    this.setState(
      (prevState) => ({
        pagination: {
          ...prevState.pagination,
          limit: nextLimit,
          page: 1,
        },
      }),
      () => this.fetchConversations(1, nextLimit)
    );
  };

  renderConversationMessages(
    conversation: Conversation,
    filteredMessages?: Conversation["conversations"],
    roleCounts?: { user: number; assistant: number; system: number },
    activeRoleFilter?: 'all' | 'user' | 'assistant' | 'system',
    messageSearchValue?: string
  ) {
    const messages = filteredMessages || conversation.conversations || [];
    const allMessages = conversation.conversations || [];
    const lastUpdated = conversation.updatedAt
      ? new Date(conversation.updatedAt)
      : null;
    const createdAt = conversation.createdAt
      ? new Date(conversation.createdAt)
      : null;
    const lastSpeaker = allMessages[allMessages.length - 1]?.role || "—";
    const characterLabel =
      conversation.character_name ||
      conversation.character_id ||
      conversation.assistant_id ||
      "—";
    const totalMessages = allMessages.length || messages.length;
    const resolvedRoleCounts =
      roleCounts || { user: 0, assistant: 0, system: 0 };
    const currentRoleFilter = activeRoleFilter || "all";
    const currentMessageSearch = messageSearchValue || "";
    const roleChipClasses: Record<string, string> = {
      all: "bg-gray-100 text-gray-700 border border-gray-200",
      user:
        "bg-rose-50 text-rose-700 border border-rose-200",
      assistant:
        "bg-blue-50 text-blue-700 border border-blue-200",
      system:
        "bg-amber-50 text-amber-700 border border-amber-200",
    };

    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-5 bg-gray-50 border-b border-gray-100">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Conversation #{conversation._id?.slice(-8) || conversation._id}
              </div>
              <div className="mt-2 text-2xl font-semibold text-gray-900">
                {characterLabel}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 text-sm text-gray-700 w-full lg:w-auto">
              <div className="bg-white rounded-xl border border-gray-200 px-3 py-2 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Messages
                </div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {totalMessages}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 px-3 py-2 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Last Speaker
                </div>
                <div className="mt-1 text-lg font-semibold text-gray-900 capitalize">
                  {lastSpeaker}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 px-3 py-2 shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Updated
                </div>
                <div className="mt-1 text-xs font-semibold text-gray-900">
                  {lastUpdated
                    ? `${lastUpdated.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })}`
                    : "—"}
                  {lastUpdated && (
                    <div className="text-[11px] font-normal text-gray-600">
                      {lastUpdated.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-t border-gray-100 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              {(["all", "user", "assistant", "system"] as const).map((type) => {
                const roleCount =
                  type === "user"
                    ? resolvedRoleCounts.user
                    : type === "assistant"
                    ? resolvedRoleCounts.assistant
                    : type === "system"
                    ? resolvedRoleCounts.system
                    : null;

                return (
                  <button
                    key={type}
                    onClick={() =>
                      this.setMessageRoleFilter(
                        type as 'all' | 'user' | 'assistant' | 'system'
                      )
                    }
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${roleChipClasses[type]} ${
                      currentRoleFilter === type
                        ? "shadow-sm ring-1 ring-[var(--primary-color)] ring-opacity-50"
                        : ""
                    }`}
                  >
                    {type === "all"
                      ? "All"
                      : type[0].toUpperCase() + type.slice(1)}{" "}
                    {roleCount !== null && (
                      <span className="text-[10px] font-medium text-gray-500 ml-1">
                        {roleCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="w-full md:w-64">
              <input
                type="text"
                value={currentMessageSearch}
                onChange={this.setMessageSearch}
                placeholder="Search message text"
                className="w-full rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {messages.map((message, index) => {
              const isUser = message.role === "user";
              const isAssistant = message.role === "assistant";
              const isSystem = message.role === "system";
              const messageKey = `${conversation._id || "conversation"}-${index}`;
              const isExpanded = Boolean(this.state.expandedMessages[messageKey]);
              const wordCount = String(message.content || "").split(" ").length;
              const bubbleClasses = isUser
                ? "border-rose-200 bg-rose-50"
                : isAssistant
                ? "border-gray-200 bg-white"
                : "border-amber-200 bg-amber-50";

              return (
                <div key={index} className="w-full">
                  <div
                    className={`w-full px-5 py-4 rounded-xl border shadow-sm transition-colors ${bubbleClasses}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            isUser
                              ? "bg-rose-500"
                              : isAssistant
                              ? "bg-blue-500"
                              : "bg-amber-500"
                          }`}
                        />
                        {message.role}
                      </span>
                      {message.timestamp && (
                        <span className="text-xs text-gray-500">
                          {new Date(message.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-900 whitespace-pre-line leading-relaxed">
                      {isSystem && !isExpanded
                        ? `${String(message.content || "")
                            .split(" ")
                            .slice(0, 18)
                            .join(" ")}${wordCount > 18 ? "..." : ""}`
                        : message.content}
                    </div>
                    {isSystem && wordCount > 18 && (
                      <button
                        type="button"
                        onClick={() => this.toggleExpandedMessage(messageKey)}
                        className="mt-2 text-xs font-semibold text-[var(--primary-color)] hover:underline"
                      >
                        {isExpanded ? "Show less" : "Show full"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {!messages.length && (
              <div className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-200 rounded-xl">
                {allMessages.length
                  ? "No messages match your filters."
                  : "No messages in this conversation yet."}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      conversations,
      selectedConversation,
      loading,
      sortBy,
      conversationSearch,
      messageSearch,
      messageRoleFilter,
      user,
      summary,
      pagination,
      error,
    } = this.state;

    // Sort conversations based on sortBy
    const sortedConversations = [...conversations].sort((a, b) => {
      if (sortBy === 'latest') {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA; // Most recent first
      } else {
        // For tokens, we'll simulate by message count (in real app, this would be token usage)
        return (b.conversations?.length || 0) - (a.conversations?.length || 0);
      }
    });

    const totalConversationsCount =
      summary.totalConversations ??
      pagination.totalConversations ??
      conversations.length;
    const totalMessages =
      summary.totalMessages ??
        conversations.reduce(
          (count, conversationItem) =>
            count + (conversationItem.conversations?.length || 0),
        0
    );
    const characterNamesFromSummary = Array.isArray(summary.characterNames)
      ? summary.characterNames.filter(
          (name): name is string => typeof name === "string" && Boolean(name)
        )
      : [];
    const fallbackCharacterNames = Array.from(
      new Set(
        conversations
          .map(
            (conversationItem) =>
              conversationItem.character_name ||
              conversationItem.character_id ||
              conversationItem.assistant_id
          )
          .filter((name): name is string => Boolean(name))
      )
    );
    const characterNames =
      characterNamesFromSummary.length > 0
        ? characterNamesFromSummary
        : fallbackCharacterNames;
    const characterCount =
      (typeof summary.characterIds === "number" && summary.characterIds >= 0
        ? summary.characterIds
        : undefined) ??
      (characterNames.length > 0
        ? characterNames.length
        : new Set(
            conversations
              .map((conversationItem) => conversationItem.character_id)
              .filter((id): id is string => Boolean(id))
          ).size);
    const characterNamesLabel = characterNames.join(", ");
    const averageMessages =
      totalConversationsCount > 0
        ? Math.round(totalMessages / totalConversationsCount)
        : 0;
    const lastActiveLabel = summary.lastUpdated
      ? new Date(summary.lastUpdated).toLocaleString()
      : (() => {
          const lastActiveConversation = sortedConversations.find(
            (conversationItem) => conversationItem.updatedAt
          );
          return lastActiveConversation?.updatedAt
            ? new Date(lastActiveConversation.updatedAt).toLocaleString()
            : "No recent activity";
        })();
    const selectedConversationId = selectedConversation?._id;
    const userName =
      user?.displayName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      "User";
    const userEmail = user?.email || "No email available";
    const userId = user?.originalId || user?._id || this.props.id;
    const {
      label: membershipBadgeLabel,
      className: membershipBadgeClassName,
    } = getMembershipBadgeMeta(
      user?.membershipBadge as MembershipBadge | undefined
    );
    const isActivelyPaying = Boolean(user?.isActivelyPaying);
    const paymentStatusLabel = isActivelyPaying
      ? "Actively paying"
      : "Not actively paying";
    const paymentStatusClassName = isActivelyPaying
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-gray-300 bg-gray-100 text-gray-600";
    const hasTokenStatus = typeof user?.ranOutOfTokens === "boolean";
    const ranOutOfTokens = Boolean(user?.ranOutOfTokens);
    const tokenStatusLabel = ranOutOfTokens
      ? "Ran out of tokens"
      : "Tokens available";
    const tokenStatusClassName = ranOutOfTokens
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";
    const totalPages =
      pagination.totalPages && pagination.totalPages > 0
        ? pagination.totalPages
        : totalConversationsCount && pagination.limit
        ? Math.ceil(totalConversationsCount / pagination.limit)
        : 0;
    const showPagination = totalPages > 1;

    const normalizedConversationSearch = conversationSearch.trim().toLowerCase();
    const filteredConversations = normalizedConversationSearch
      ? sortedConversations.filter((conversationItem) => {
          const characterName =
            conversationItem.character_name ||
            conversationItem.character_id ||
            conversationItem.assistant_id ||
            "";
          const latestMessagePreview = String(
            conversationItem.conversations?.[
              conversationItem.conversations.length - 1
            ]?.content || ""
          );
          const haystack = [
            characterName,
            conversationItem._id,
            latestMessagePreview,
          ]
            .join(" ")
            .toLowerCase();

          return haystack.includes(normalizedConversationSearch);
        })
      : sortedConversations;

    const normalizedMessageSearch = messageSearch.trim().toLowerCase();
    const allMessages = selectedConversation?.conversations || [];
    const filteredMessages = selectedConversation
      ? allMessages.filter((message) => {
          const matchesRole =
            messageRoleFilter === "all" || message.role === messageRoleFilter;
          const content =
            typeof message.content === "string" ? message.content : "";
          const matchesSearch = normalizedMessageSearch
            ? content.toLowerCase().includes(normalizedMessageSearch)
            : true;
          return matchesRole && matchesSearch;
        })
      : [];
    const roleCounts = allMessages.reduce(
      (acc: { user: number; assistant: number; system: number }, message) => {
        if (message.role === "user") acc.user += 1;
        else if (message.role === "assistant") acc.assistant += 1;
        else if (message.role === "system") acc.system += 1;
        return acc;
      },
      { user: 0, assistant: 0, system: 0 }
    );

    if (loading && !conversations.length) {
      return (
        <div className="admin">
          <AdminHeader />
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">Loading conversations...</div>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="admin">
          <AdminHeader />
          <div className="edit-character px-4 sm:px-6 lg:px-10">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="bg-white rounded-2xl shadow p-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      User
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900">
                      {userName}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {userEmail}
                    </div>
                  
                  </div>
                  <div className="flex flex-col sm:items-end gap-3">
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <Link
                        to="/admin/messages"
                        className="inline-flex items-center justify-center bg-[var(--primary-color)] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors shadow-md"
                      >
                        Back to Users
                      </Link>
                      {userId && (
                        <Link
                          to={`/admin/user/${userId}`}
                          className="inline-flex items-center justify-center bg-[var(--secondary-color)] text-gray-800 px-4 py-2 rounded-md border border-gray-200 hover:bg-[#eadde3] transition-colors shadow-md"
                        >
                          View User Profile
                        </Link>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                        Lookup ID
                      </div>
                      <div className="text-sm font-mono text-gray-700 bg-gray-100 px-3 py-1 rounded-lg">
                        {userId}
                      </div>
                      {user?._id && user?._id !== userId && (
                        <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1 rounded-lg border border-gray-200">
                          Record: {user._id}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 sm:text-right">
                      Last activity:{" "}
                      <span className="font-medium text-gray-700">
                        {lastActiveLabel}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="bg-white rounded-2xl shadow p-5">
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Total Conversations
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-gray-900">
                      {totalConversationsCount}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Across {characterCount} character
                      {characterCount === 1 ? "" : "s"}
                    </div>
                    {characterNamesLabel && (
                      <div className="mt-1 text-xs text-gray-400">
                        {characterNamesLabel}
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl shadow p-5">
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Total Messages
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-gray-900">
                      {totalMessages}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Average {averageMessages} per conversation
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl shadow p-5">
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Characters Engaged
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-gray-900">
                      {characterCount}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      {characterNamesLabel || "Character roster unavailable"}
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl shadow p-5">
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      Status Badges
                    </div>
                    <div className="mt-3 flex flex-wrap items-start gap-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${membershipBadgeClassName}`}
                      >
                        {membershipBadgeLabel}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${paymentStatusClassName}`}
                      >
                        {paymentStatusLabel}
                      </span>
                      {hasTokenStatus && (
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tokenStatusClassName}`}
                        >
                          {tokenStatusLabel}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Snapshot of membership, payment, and token status
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:pr-6">
                {/* Conversations List */}
                <div className="bg-white shadow rounded-2xl lg:col-span-5 xl:col-span-4">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex flex-col gap-4 mb-4">
                      <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between md:gap-4">
                        <div>
                          <h3 className="text-lg leading-6 font-semibold text-gray-900">
                            Conversations
                          </h3>
                          <p className="text-sm text-gray-500">
                            Filter, sort, and jump between threads.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="flex gap-2 bg-gray-100 rounded-full p-1">
                            <button
                              onClick={() => this.setSortBy('latest')}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                sortBy === 'latest'
                                  ? 'bg-white text-[var(--primary-color)] shadow-sm'
                                  : 'text-gray-700 hover:text-[var(--primary-color)]'
                              }`}
                            >
                              Latest
                            </button>
                            <button
                              onClick={() => this.setSortBy('tokens')}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                                sortBy === 'tokens'
                                  ? 'bg-white text-[var(--primary-color)] shadow-sm'
                                  : 'text-gray-700 hover:text-[var(--primary-color)]'
                              }`}
                            >
                              Most Tokens
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                            <span>Show</span>
                            <select
                              value={pagination.limit}
                              onChange={this.changeLimit}
                              className="py-0.5 px-1.5 border-none bg-transparent rounded-md text-xs focus:outline-none focus:ring-0"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span>per page</span>
                          </div>
                        </div>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={conversationSearch}
                          onChange={this.setConversationSearch}
                          placeholder="Search by character or message"
                          className="w-full rounded-full border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                      {error ? (
                        <div className="text-sm text-red-600 text-center py-10 border border-red-200 rounded-xl bg-red-50">
                          {error}
                        </div>
                      ) : filteredConversations.length ? (
                        filteredConversations.map((conversation: Conversation) => {
                          const messageCount =
                            conversation.conversations?.length || 0;
                          const lastUserMessage = (() => {
                            if (!conversation.conversations?.length) {
                              return null;
                            }

                            for (
                              let index = conversation.conversations.length - 1;
                              index >= 0;
                              index -= 1
                            ) {
                              const currentMessage =
                                conversation.conversations[index];

                              if (currentMessage.role === "user") {
                                return currentMessage;
                              }
                            }

                            return null;
                          })();
                          const lastUpdated = conversation.updatedAt
                            ? new Date(conversation.updatedAt)
                            : null;
                          const isSelected =
                            selectedConversationId === conversation._id;
                          const previewMessage =
                            lastUserMessage?.content ||
                            conversation.conversations?.[
                              conversation.conversations.length - 1
                            ]?.content ||
                            "No user messages yet.";

                          return (
                            <button
                              key={conversation._id}
                              type="button"
                              onClick={() => this.selectConversation(conversation)}
                              className={`w-full text-left p-4 rounded-2xl border transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                isSelected
                                  ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 focus:ring-[var(--primary-color)] shadow-sm"
                                  : "border-gray-200 hover:border-[var(--primary-color)] hover:bg-[var(--secondary-color)]/60 focus:ring-[var(--primary-color)] focus:ring-opacity-40 bg-white"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`h-2.5 w-2.5 rounded-full ${
                                      isSelected
                                        ? "bg-[var(--primary-color)]"
                                        : "bg-rose-500"
                                    }`}
                                  />
                                  <div className="text-sm font-semibold text-gray-900">
                                    Conversation {conversation._id?.slice(-8)}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 text-right">
                                  {lastUpdated
                                    ? lastUpdated.toLocaleDateString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric"
                                      })
                                    : "Not updated"}
                                  <div className="mt-0.5">
                                    {lastUpdated
                                      ? lastUpdated.toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit"
                                        })
                                      : ""}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2 text-xs uppercase tracking-wider text-gray-500">
                                Character:{" "}
                                <span className="font-medium text-gray-700 normal-case">
                                  {conversation.character_name ||
                                    conversation.character_id ||
                                    conversation.assistant_id ||
                                    "—"}
                                </span>
                              </div>
                              <div className="mt-2 text-sm text-gray-700 overflow-hidden max-h-14">
                                {previewMessage}
                              </div>
                              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                                <span className="inline-flex items-center gap-1">
                                  <span className="h-2 w-2 rounded-full bg-gray-400" />
                                  {messageCount} message
                                  {messageCount === 1 ? "" : "s"}
                                </span>
                                <span>
                                  {lastUserMessage?.timestamp
                                    ? new Date(
                                        lastUserMessage.timestamp
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit"
                                      })
                                    : ""}
                                </span>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-10 border border-dashed border-gray-200 rounded-xl">
                          {normalizedConversationSearch
                            ? "No conversations match your search."
                            : "No conversations available for this user."}
                        </div>
                      )}
                    </div>
                    {!error && showPagination && (
                      <div className="mt-4 flex justify-center">
                        <nav className="relative z-0 inline-flex rounded-full shadow-sm overflow-hidden border border-gray-200">
                          {Array.from({ length: totalPages }, (_, index) => {
                            const pageNumber = index + 1;
                            const isActive = pagination.page === pageNumber;

                            return (
                              <button
                                key={pageNumber}
                                type="button"
                                onClick={() => this.changePage(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 text-xs font-medium transition-colors ${
                                  isActive
                                    ? "bg-[var(--primary-color)] text-white"
                                    : "bg-white text-gray-600 hover:bg-[var(--secondary-color)]"
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </nav>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conversation Details */}
                <div className="lg:col-span-7 xl:col-span-8 lg:pr-2">
                  {selectedConversation ? (
                    this.renderConversationMessages(
                      selectedConversation,
                      filteredMessages,
                      roleCounts,
                      messageRoleFilter,
                      messageSearch
                    )
                  ) : (
                    <div className="bg-white p-6 rounded-lg shadow flex items-center justify-center h-64 border-2 border-dashed border-[var(--secondary-color)]">
                      <div className="text-center">
                        <div className="text-gray-500 text-lg mb-2">
                          Select a conversation
                        </div>
                        <div className="text-gray-400 text-sm">
                          Click on a conversation from the list to view messages
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(UserMessages);
